import React, { useEffect } from "react";
import toast from "react-hot-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetChecklistQuery, useSkipChecklistItemMutation } from "@/store/rest_api";
import ChecklistRow from "./ChecklistRow";

interface OnboardingChecklistProps {
  onComplete?: () => void;
}

export default function OnboardingChecklist({ onComplete }: OnboardingChecklistProps) {
  const { data, isLoading } = useGetChecklistQuery();
  const [skipItem] = useSkipChecklistItemMutation();

  const handleSkip = async (itemKey: string) => {
    try {
      await skipItem(itemKey).unwrap();
    } catch {
      toast.error("Failed to skip item");
    }
  };

  useEffect(() => {
    if (data && data.progress === 100) {
      onComplete?.();
    }
  }, [data, onComplete]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.items.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between space-y-0">
        <div className="space-y-1">
          <CardTitle>Get started</CardTitle>
          <p className="text-sm text-muted-foreground">
            {data.completedCount} of {data.totalCount} complete
          </p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-semibold">{data.progress}%</div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <Progress value={data.progress} />
        <div className="divide-y">
          {data.items.map((item) => (
            <ChecklistRow key={item.key} item={item} onSkip={() => handleSkip(item.key)} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
